import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { usePeriodStore } from '@/stores/periodStore'
import { PeriodService } from '@/services/periodService'
import { Calendar } from 'lucide-react'
import { Button } from '@/components/ui/button'

/**
 * PeriodDetailPage - Redirect handler at /periods/:periodId
 *
 * Sets the current period in the store and redirects to the appropriate
 * wizard step based on period status:
 * - Draft periods → /periods/:periodId/upload (wizard step 1)
 * - Locked periods → /periods/:periodId/export (wizard step 4)
 */
export function PeriodDetailPage() {
  const { periodId } = useParams<{ periodId: string }>()
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const { setCurrentPeriod } = usePeriodStore()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!currentUser || !periodId) return

    const loadAndRedirect = async () => {
      try {
        setLoading(true)
        setError(null)

        // Fetch period details
        const periodData = await PeriodService.getPeriod(currentUser, periodId)

        if (!periodData) {
          setError('Perioden hittades inte')
          setLoading(false)
          return
        }

        // Set as current period in store
        setCurrentPeriod(periodId)

        // Redirect to wizard step based on period status
        if (periodData.status === 'locked') {
          // Locked periods go to export step
          navigate(`/periods/${periodId}/export`, { replace: true })
        } else {
          // Draft periods go to upload step
          navigate(`/periods/${periodId}/upload`, { replace: true })
        }
      } catch (err) {
        console.error('Error loading period:', err)
        setError('Det gick inte att ladda perioden')
        setLoading(false)
      }
    }

    loadAndRedirect()
  }, [currentUser, periodId, navigate, setCurrentPeriod])

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground animate-pulse" />
          <p className="text-muted-foreground">Laddar period...</p>
        </div>
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-12">
        <Calendar className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
        <p className="text-red-600 mb-4">{error}</p>
        <Button onClick={() => navigate('/periods')}>
          Tillbaka till perioder
        </Button>
      </div>
    )
  }

  // This shouldn't render normally since we redirect immediately
  // But keep it as fallback
  return (
    <div className="text-center py-12">
      <Calendar className="h-16 w-16 mx-auto mb-4 text-muted-foreground" />
      <p className="text-muted-foreground">Omdirigerar...</p>
    </div>
  )
}
